"use client";

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import SupportTicketsAdmin from "./admin/SupportTicketsAdmin";
import HelpCenterAdmin from "./admin/HelpCenterAdmin";
import AssingTask from "./scribe-tasks/AssingTask.jsx";

import UserSupportTickets from "./support/UserSupportTickets";
import { ProfileSettings } from "../components/profile-settings";
import { BillingSettings } from "../components/billing-settings";
// import { DefaultsSettings } from "../components/defaults-settings"
// import { NotificationSettings } from "../components/notification-settings"
import { PracticePartnersSettings } from "../components/practice-partners-settings";
import { Admin } from "./admin/admin.jsx";
import PaymentsAdmin from "./admin/PaymentsAdmin.jsx";
import { authService } from "../apis/authService";
import settingsService from "../apis/settingsService";
import { AudioSettings } from "../components/audio-settings.jsx";
import FeatureSettings from "../components/feature-settings.jsx";
import { notificationService } from "../apis/notificationService";
import {
  User,
  DollarSign,
  FileText,
  Bell,
  UserCog,
  Settings,
  LifeBuoy,
  MessageSquare,
  BookOpen,
  Users,
  AudioLines,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";

export function SettingsHeader({ mode = "desktop" }) {
  const [activeTab, setActiveTab] = useState("Account");
  const [mobileSection, setMobileSection] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [user, setUser] = useState(null);
  const [settingsOptions, setSettingsOptions] = useState({});
  const [specialtiesList, setSpecialties] = useState([]);
  const [countriesList, setCountries] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInit = async () => {
      const [me, opts] = await Promise.all([
        authService.getMe(),
        settingsService.getSettingsOptions().catch(() => ({})),
      ]);
      setUser(me.data);
      const optionsData = opts || {};
      setSettingsOptions(optionsData);
      const list = optionsData?.specialties || [];
      setSpecialties(Array.isArray(list) ? list : []);
      const clist = optionsData?.countries || [];
      setCountries(Array.isArray(clist) ? clist : []);
    };
    fetchInit();
  }, []);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") {
        setActiveTab("Track Tickets");
      } else {
        setActiveTab("Track Support Tickets");
      }
    }
  }, [user]);

  // Fetch notification count for admin users
  useEffect(() => {
    if (!user || user.role !== "admin") return;

    const fetchNotificationCount = async () => {
      try {
        const response = await notificationService.getUnreadCount();
        if (response.success) {
          setNotificationCount(response.data?.unreadCount || 0);
        }
      } catch (error) {
        console.error("Failed to fetch notification count:", error);
      }
    };

    // Initial fetch
    fetchNotificationCount();

    // Poll every 30 seconds for real-time updates
    const interval = setInterval(fetchNotificationCount, 30000);

    return () => clearInterval(interval);
  }, [user]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setIsMinimized(false);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Removed automatic minimization on mouse leave
    // Sidebar will now only close on click
  };

  const tabs = [
    {
      label: "Track Tickets",
      category: "Support",
      icon: <LifeBuoy className="w-4 h-4" />,
      requiresAdmin: true,
    },
    {
      label: "Admin Panel",
      category: "Support",
      icon: <UserCog className="w-4 h-4" />,
      requiresAdmin: true,
    },
    {
      label: "Payments",
      category: "Support",
      icon: <DollarSign className="w-4 h-4" />,
      requiresAdmin: true,
    },
    // {
    //   label: "Assign Task",
    //   category: "Support",
    //   icon: <FileText className="w-4 h-4" />,
    //   requiresAdmin: true,
    //   showBadge: true
    // },
    {
      label: "Help Center",
      category: "Support",
      icon: <BookOpen className="w-4 h-4" />,
      requiresAdmin: true,
    },
    {
      label: "Track Support Tickets",
      category: "Support",
      icon: <LifeBuoy className="w-4 h-4" />,
    },
    {
      label: "Account",
      category: "Personal",
      icon: <User className="w-4 h-4" />,
    },
    {
      label: "Providers Directory",
      category: "Personal",
      icon: <Users className="w-4 h-4" />,
    },
    {
      label: "Billing",
      category: "Personal",
      icon: <DollarSign className="w-4 h-4" />,
      requiresAdmin: true,
    },
    // { label: "Defaults", category: "Personal", icon: <FileText className="w-4 h-4" /> },
    // { label: "Notifications", category: "Personal", icon: <Bell className="w-4 h-4" /> },
    {
      label: "Audio Settings",
      category: "Personal",
      icon: <AudioLines className="w-4 h-4" />,
    },
  ];

  const isAdmin = user?.role === "admin";
  const visibleTabs = tabs.filter(
    (tab) =>
      (!tab.requiresAdmin || isAdmin) &&
      !(isAdmin && tab.label === "Track Support Tickets"),
  );

  const groupedTabs = visibleTabs.reduce((acc, tab) => {
    acc[tab.category] = acc[tab.category] || [];
    acc[tab.category].push(tab);
    return acc;
  }, {});

  // Define route mappings for each tab
  const getTabRoute = (tabLabel) => {
    const routes = {
      // Admin routes
      "Track Tickets": "/scribe/settings?tab=track-tickets",
      "Admin Panel": "/scribe/settings?tab=admin-panel",
      Payments: "/scribe/settings?tab=payments",
      "Assign Task": "/scribe/settings?tab=assign-task",
      "Help Center": "/scribe/settings?tab=help-center",

      // User routes
      "Track Support Tickets": "/scribe/settings?tab=support-tickets",

      // Personal settings routes
      Account: "/scribe/settings?tab=account",
      "Providers Directory": "/scribe/settings?tab=providers-database",
      Billing: "/scribe/settings?tab=billing",
      // "Defaults": "/scribe/settings?tab=defaults",
      // "Notifications": "/scribe/settings?tab=notifications",

      // Audio Settings route,

      "Audio Settings": "/scribe/settings?tab=audio-settings",
    };

    return routes[tabLabel] || "/scribe/settings";
  };

  // Handle tab clicks with modifier keys
  const handleTabClick = useCallback(
    (e, tabLabel) => {
      // Prevent right-click from triggering regular click
      if (e.button === 2) return;

      const route = getTabRoute(tabLabel);

      // Regular click = Switch to tab
      setActiveTab(tabLabel);
      if (tabLabel === "Admin Panel") {
        // Lets an open user profile return to the user list.
        window.dispatchEvent(new Event("admin-panel-tab-click"));
      }
      const url = new URL(window.location);
      const searchParams = new URLSearchParams(url.search);
      searchParams.set("tab", tabLabel.toLowerCase().replace(/\s+/g, "-"));
      navigate(`${url.pathname}?${searchParams.toString()}`);

      // For mobile view, set the mobile section when clicking a tab
      if (window.innerWidth < 768 && mobileSection !== tabLabel) {
        setMobileSection(tabLabel);
      }
    },
    [navigate, mobileSection],
  );

  const renderContent = () => {
    switch (activeTab) {
      case "Admin Panel":
        return <Admin />;
      case "Payments":
        return <PaymentsAdmin />;
      case "Track Tickets":
        return <SupportTicketsAdmin />;
      // case "Assign Task":
      //   return <AssingTask />
      case "Help Center":
        return <HelpCenterAdmin />;
      case "Track Support Tickets":
        return <UserSupportTickets />;
      case "Account":
        return (
          <ProfileSettings
            specialties={specialtiesList}
            countries={countriesList}
            options={settingsOptions}
          />
        );
      case "Providers Directory":
        return <PracticePartnersSettings />;
      case "Billing":
        return <BillingSettings />;
      // case "Defaults":
      //   return <DefaultsSettings />
      // case "Notifications":
      //   return <NotificationSettings />
      case "Audio Settings":
        return <AudioSettings />;
      default:
        return (
          <div className="text-gray-500 text-sm">
            {activeTab} section is under development.
          </div>
        );
    }
  };

  // Desktop sidebar tab item - WITH BADGE SUPPORT
  const SidebarTabItem = ({ tab, badgeCount = 0 }) => {
    const isActive = activeTab === tab.label;

    return (
      <li
        key={uuidv4()}
        className={`flex gap-2 items-center text-sm cursor-pointer rounded-lg transition-colors px-3 py-2 ${
          isActive
            ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800/50"
        }`}
        onClick={(e) => handleTabClick(e, tab.label)}
      >
        <span className="flex-shrink-0 relative">
          {tab.icon}
          {/* ADD BADGE FOR ASSIGN TASK TAB */}
          {tab.label === "Assign Task" && badgeCount > 0 && (
            <span
              className={`
              absolute -top-1 -right-1 bg-red-600 text-white text-xs 
              ${isMinimized ? "h-2 w-2" : "h-5 w-5"} 
              rounded-full flex items-center justify-center border border-white
            `}
            >
              {!isMinimized && (badgeCount > 9 ? "9+" : badgeCount)}
            </span>
          )}
        </span>
        {!isMinimized && <span className="truncate flex-1">{tab.label}</span>}
        {/* Context menu indicator */}
        {!isMinimized && (
          <span className="ml-2 text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
            ⋮
          </span>
        )}
      </li>
    );
  };

  // ─── Mobile horizontal scroll helpers ───────────────────────────────────────
  const updateScrollButtons = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons, { passive: true });
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      ro.disconnect();
    };
  }, [updateScrollButtons, visibleTabs]);

  // Scroll the tab strip left or right by ~160px
  const scrollTabs = (direction) => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const amount = Math.min(el.clientWidth * 0.6, 220);

    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  // Scroll the active tab into view whenever activeTab changes (mobile only)
  // Center active tab on mobile – run on mount and after activeTab changes
  useLayoutEffect(() => {
    // Only for mobile view
    if (window.innerWidth >= 768) return;

    // Small delay to ensure layout is complete
    const timer = setTimeout(() => {
      const el = scrollContainerRef.current;
      if (!el) return;

      const activeEl = el.querySelector("[data-active='true']");
      if (!activeEl) return;

      const containerRect = el.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();

      const offset =
        activeRect.left -
        containerRect.left -
        containerRect.width / 2 +
        activeRect.width / 2;

      el.scrollBy({
        left: offset,
        behavior: "smooth",
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [activeTab, visibleTabs]); // Re-run when activeTab changes or tabs list changes
  // ────────────────────────────────────────────────────────────────────────────

  // Handle URL on initial load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get("tab");

    if (tabParam) {
      // Convert param back to tab label format
      const tabLabel = visibleTabs.find(
        (tab) => tab.label.toLowerCase().replace(/\s+/g, "-") === tabParam,
      )?.label;

      if (tabLabel) {
        setActiveTab(tabLabel);
      }
    }
  }, [visibleTabs]);

  // Desktop Layout (md and above)
  if (mode === "desktop" && window.innerWidth >= 768) {
    return (
      // Outer container takes full height from parent
      <div className="flex h-full w-full">
        {/* Sidebar – no sticky, no h-screen, just h-full */}
        <div
          className={`${isMinimized ? "w-16" : "w-56"} bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col transition-all duration-300 ease-in-out h-full flex-shrink-0`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div>
            {isMinimized ? (
              <Settings className="w-4 h-4 m-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <h1 className="text-lg font-semibold m-3 text-gray-900 dark:text-gray-100">
                Settings
              </h1>
            )}
          </div>

          {/* Sidebar tabs – scroll only if many items */}
          <div className="space-y-6 p-2 flex-1 overflow-y-auto">
            {Object.entries(groupedTabs).map(([category, categoryTabs]) => (
              <div key={uuidv4()}>
                {!isMinimized && (
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 m-1">
                    {category}
                  </h4>
                )}
                <ul className="space-y-1">
                  {categoryTabs.map((tab) => (
                    <SidebarTabItem
                      key={uuidv4()}
                      tab={tab}
                      badgeCount={
                        tab.label === "Assign Task" ? notificationCount : 0
                      }
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Minimize toggle */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="w-full text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center justify-center gap-1"
              title={isMinimized ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isMinimized ? "→" : "←"} {!isMinimized && "Collapse"}
            </button>
          </div>
        </div>

        {/* Main content – scrolls independently */}
        <main className="flex-1 p-6 overflow-y-auto h-full text-base">
          {renderContent()}
        </main>
      </div>
    );
  }

  // Mobile view (below md) - with horizontal scrollable tabs
  return (
    <div className="flex flex-col h-full">
      {/* Settings Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-10">
        <h1 className="text-lg font-semibold p-4 text-gray-900 dark:text-gray-100">
          Settings
        </h1>
      </div>

      {/* Horizontal scrollable tab header */}
      <div className="flex items-center bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-10">
        {/* Left chevron */}
        <button
          onClick={() => {
            const currentIdx = visibleTabs.findIndex(
              (tab) => tab.label === activeTab,
            );
            if (currentIdx > 0) {
              handleTabClick({ button: 0 }, visibleTabs[currentIdx - 1].label);
            }
          }}
          disabled={
            visibleTabs.findIndex((tab) => tab.label === activeTab) <= 0
          }
          className="flex-shrink-0 flex items-center justify-center w-8 h-11 transition-colors"
          aria-label="Previous tab"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Scrollable tab strip */}
        <div
          ref={scrollContainerRef}
          className="flex-1 flex overflow-x-auto"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.label;
            const badgeCount =
              tab.label === "Assign Task" ? notificationCount : 0;
            return (
              <button
                key={tab.label}
                data-active={isActive}
                onClick={(e) => handleTabClick(e, tab.label)}
                className={`relative flex-shrink-0 flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                  isActive
                    ? "border-blue-600 text-blue-700 dark:text-blue-400 dark:border-blue-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                <span className="relative flex-shrink-0">
                  {/* {tab.icon} */}
                  {/* Badge for Assign Task */}
                  {tab.label === "Assign Task" && badgeCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs h-4 w-4 rounded-full flex items-center justify-center border border-white">
                      {badgeCount > 9 ? "9+" : badgeCount}
                    </span>
                  )}
                </span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Right chevron */}
        <button
          onClick={() => {
            const currentIdx = visibleTabs.findIndex(
              (tab) => tab.label === activeTab,
            );
            if (currentIdx < visibleTabs.length - 1) {
              handleTabClick({ button: 0 }, visibleTabs[currentIdx + 1].label);
            }
          }}
          disabled={
            visibleTabs.findIndex((tab) => tab.label === activeTab) >=
            visibleTabs.length - 1
          }
          className="flex-shrink-0 flex items-center justify-center w-8 h-11 transition-colors"
          aria-label="Next tab"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <main className="flex-1 p-4 overflow-y-auto bg-gray-50 dark:bg-gray-950 text-base">
        {renderContent()}
      </main>
    </div>
  );
}